"use client";

import { useState } from "react";

import { AdSlot } from "./AdSlot";

export type AppMode = "practice" | "cpu" | "custom" | "rooms" | "casual" | "ranked";

type HomeMenuProps = {
  installable?: boolean;
  onInstall?: () => void;
  onOpenKeyConfig: () => void;
  onSelect: (mode: AppMode) => void;
};

type MenuItem = {
  mode: AppMode;
  icon: string;
  title: string;
  description: string;
  badge?: string;
};

type LegacyInfoTopic = "howto" | "manners" | "ranks" | "rooms" | "rotation" | "faq";

const rotationPieces = [
  { id: "rotation-o", name: "黄色 / O型", src: "/rotation-guide/o-yellow.gif" },
  { id: "rotation-t", name: "水色 / T型", src: "/rotation-guide/t-cyan.gif" },
  { id: "rotation-j", name: "青色 / J型", src: "/rotation-guide/j-blue.gif" },
  { id: "rotation-l", name: "橙色 / L型", src: "/rotation-guide/l-orange.gif" },
  { id: "rotation-z", name: "緑色 / Z型", src: "/rotation-guide/z-green.gif" },
  { id: "rotation-s", name: "紫色 / S型", src: "/rotation-guide/s-purple.gif" },
  { id: "rotation-i", name: "赤色 / I型", src: "/rotation-guide/i-red.gif" },
] as const;

const rotationTargetPrefixes = [
  ["黄色・O型", "rotation-o"],
  ["水色・T型", "rotation-t"],
  ["青色・J型", "rotation-j"],
  ["橙色・L型", "rotation-l"],
  ["緑色・Z型", "rotation-z"],
  ["紫色・S型", "rotation-s"],
  ["赤色・I型", "rotation-i"],
] as const;

const rotationTechniqueGifs = [
  { id: "user-t-gap", prefix: "水色・段差入れ込み：", label: "水色・段差入れ込み", src: "/rotation-guide/techniques/user-t-gap.gif" },
  { id: "user-j-step", prefix: "青色・段差回転：", label: "青色・段差回転", src: "/rotation-guide/techniques/user-j-step.gif" },
  { id: "user-l-lower-turn", prefix: "橙色・下端回転：", label: "橙色・下端回転", src: "/rotation-guide/techniques/user-l-lower-turn.gif" },
  { id: "user-i-right-wall", prefix: "赤色・右壁際回転：", label: "赤色・右壁際回転", src: "/rotation-guide/techniques/user-i-right-wall.gif" },
  { id: "user-l-right-wall", prefix: "橙色・右壁際入れ込み：", label: "橙色・右壁際入れ込み", src: "/rotation-guide/techniques/user-l-right-wall.gif" },
  { id: "user-j-left-wall", prefix: "青色・左壁際入れ込み：", label: "青色・左壁際入れ込み", src: "/rotation-guide/techniques/user-j-left-wall.gif" },
  { id: "user-z-combo", prefix: "紫色・複合地形入れ：", label: "紫色・複合地形入れ", src: "/rotation-guide/techniques/user-z-combo.gif" },
  { id: "user-t-center-gap", prefix: "水色・中央段差入れ：", label: "水色・中央段差入れ", src: "/rotation-guide/techniques/user-t-center-gap.gif" },
  { id: "user-z-complex", prefix: "紫色・複合地形回転：", label: "紫色・複合地形回転", src: "/rotation-guide/techniques/user-z-complex.gif" },
] as const;

function rotationTargetForLine(line: string): string | undefined {
  return rotationTargetPrefixes.find(([prefix]) => line.startsWith(prefix))?.[1];
}

function rotationTechniqueForLine(line: string) {
  return rotationTechniqueGifs.find(({ prefix }) => line.startsWith(prefix));
}

const legacyInfo = {
  howto: {
    kicker: "HOW TO PLAY",
    title: "遊び方",
    lines: [
      "1人で練習では、落ちものブロックの操作と盤面を自分のペースで確認できます。",
      "CPUと対戦では、CPU相手に実戦形式で練習できます。",
      "マルチルームでは、同じROOM番号または部屋IDの人と対戦できます。",
    ],
  },
  manners: {
    kicker: "ROOM MANNERS",
    title: "マナー",
    lines: [
      "相手への連続送信や、対戦を妨げる行為は控えてください。",
      "ルームチャットでは、相手が読みやすい短いメッセージを心がけてください。",
      "P2P対戦ではゲーム中の盤面をサーバーへ送信しません。",
    ],
  },
  ranks: {
    kicker: "RANK GUIDE",
    title: "段位について",
    lines: [
      "さて、今回は段位の説明をします。",
      "段位は全部で18種類存在し、5つのランクに分類されます。段位は以下の通りです。",
      "5級、4級、3級、2級、1級、初段、2段、3段、4段、5段、6段、7段、8段、9段、将軍、元帥、西川、神",
      "Eランク",
      "最下位ランクで、段位は「5級」〜「1級」が相当します。",
      "このランクが昇格するには「経験値」を溜めなければなりません。",
      "勝利した場合の経験値は、相手との段位差が大きいほど多くもらえます。",
      "負けた場合でも3ptもらえますが、昇格時のみ、勝利しなければ増えません。",
      "「4級」に昇格するには100pt、「3級」に昇格するには200pt。",
      "「2級」に昇格するには300pt、「1級」に昇格するには500ptの経験値が必要です。",
      "経験値が800pt以上溜まると「Dランク」である「初段」に昇格します。",
      "Dランク",
      "このランクは「初段」〜「3段」が相当します。",
      "このランクが昇格するには、「Dランク」の人に2連勝しなければなりません。",
      "また、「Dランク」の人に6連敗すると、降格してしまいます。",
      "ただし、初段の人が1級に降格することはありません。",
      "Cランク",
      "このランクは「4段」〜「6段」が相当します。",
      "このランクが昇格するには、「Cランク」の人に4連勝しなければなりません。",
      "また、「Cランク」の人に5連敗すると、降格してしまいます。",
      "Bランク",
      "このランクは「7段」〜「9段」が相当します。",
      "このランクが昇格するには、「Bランク」の人に6連勝しなければなりません。",
      "また、「Bランク」の人に4連敗すると、降格してしまいます。",
      "Aランク",
      "このランクは「将軍」「元帥」「西川」が相当します。",
      "このランクが昇格するには、「Aランク」の人に8連勝しなければなりません。",
      "また、「Aランク」または「Sランク」の人に3連敗すると、降格してしまいます。",
      "Sランク",
      "このランクは「神」が相当します。",
      "このランクでは昇降はありませんが、連勝連敗数がカウントされます。",
      "ただし、「Eランク」に負けた場合は連敗にカウントされません。",
      "「Eランク」〜「Aランク」の人が「Sランク」に勝利すると、「神ボーナス」がもらえます。",
      "神ボーナス",
      "「Eランク」が「神」に勝利すると、その時点で「初段」に昇格します（飛び級可能）。",
      "「Dランク」、「Cランク」が「神」に勝利すると、1段昇格できます。",
      "「Bランク」が「神」に勝利すると、「3連勝」分加算されます。昇格した場合、端数は無効です。",
      "「Aランク」が「神」に勝利すると、「2連勝」分加算されます。昇格した場合、端数は無効です。",
      "「Dランク」〜「Bランク」では、「神」に負けても連敗数はカウントされませんが、「Aランク」では「神」に負けても連敗数がカウントされます。",
      "※現在のランクマッチ機能は準備中です。",
    ],
  },
  rooms: {
    kicker: "ROOM GUIDE",
    title: "ルームについて",
    lines: [
      "ログイン時に選択可能なRoomは、全部で7種類ありますが、それぞれに特徴があります。",
      "ROOM1は初心者限定の部屋で、「名無し」もしくはEランクの人のみ入室可能です。ROOM1で初段になった場合は、ログアウトすると戻って来られなくなります。",
      "ROOM2〜ROOM4は、すべての人が入室可能です。主にこの3部屋がメインに使われます。",
      "ROOM5に入るには、Dランク以上の段位が必要です。",
      "ROOM6に入るには、Bランク以上の段位が必要です。ROOM6で6段に降格した場合は、ログアウトすると7段以上に戻るまで入室できません。",
      "精神と時の部屋は誰でも入室可能な秘密の部屋です。ログイン人数は、この部屋に入らない限りわかりません。",
      "精神と時の部屋では勝敗が影響せず、戦績も変化しません。戦績を気にせず気楽に勝負できます。",
      "P2P対戦では、ゲーム中の盤面をサーバーへ送信しません。",
    ],
  },
  rotation: {
    kicker: "ROTATION RULES",
    title: "回転法則",
    theory: [
      "ブロックワクワクフロンティアでは原作であるブロックワクワクキングダムに倣って以下の回転法則で動作します。",
      "ブロックワクワクキングダムでは、中心軸を基準とした回転を行います。",
      "この回転で、回転後の形が地形に重なる場合は以下の順に壁蹴り判定が起こります。",
      "左1＞左2＞右1＞右2＞下1＞下1＋左1＞下1＋左2＞下1＋右1＞下1＋右2",
      "左1、とは左に1マスずらす、という意味で、全て通常の回転地からの相対位置です。",
      "まぁ、難しく考えずに形で覚えれば十分です。（原文ママ）",
    ],
    lines: [
      "ここでは各ブロックの回転を、原作の色分けと同じ順番で整理します。基礎を中心にした説明なので、地形ごとの応用は盤面を見ながら覚えていきましょう。",
      "GIF内では、灰色が積み上がった地形、色付きが操作するテトリミノです。技の名前と盤面での入り方を見比べてください。",
      "以下の実操作GIFは、盤面エディタで記録したものです。説明用の仮称で整理しており、Tスピンなど特定の派生技とは断定していません。",
      "上方向へ持ち上げるキックはありません。床際で回転できないとき、ブロックを上へ逃がして回すことはできません。",
      "候補の4マスが盤面の外へ出る、または積み上がったブロックと1マスでも重なる場合、その候補は不成立です。",
      "10個の候補が全て不成立なら、回転は起きず、元の姿勢と位置のまま残ります。回転失敗時に左右移動を自動追加することもありません。",
      "盤面は横10マス×縦24マス。ブロックは4×4の基準枠で管理し、出現時の基準座標はx=4、y=1です。",
      "回転状態は0・1・2・3の4段階です。右回転は 0→1→2→3→0、左回転は 0→3→2→1→0 の順で切り替わります。",
      "180度回転専用の操作はありません。反対向きにする場合も、90度回転を2回行います。",
      "回転入力は固定ゲーム時計のサンプル境界で判定され、押しっぱなしで毎フレーム連続回転はしません。1回転したら、いったんボタンを離して再入力します。",
      "黄色・O型（四角）：原作では回転テクニックが存在しません。4つの回転状態で同じ2×2配置なので、回転入力をしても見た目は変わりません。",
      "水色・T型：4つの姿勢がそれぞれ異なります。段差へ差し込むときは、回転後の4マスと候補順を確認します。",
      "水色・段差入れ込み：水色T型を左側の段差へ落とし、着地前に姿勢を変えて空きへ収める実盤面の記録です。Tスピンとは断定せず、回転候補で成立する段差入れとして掲載します。",
      "水色・中央段差入れ：紫色・橙色の隙間へ水色T型を落とし、中央で姿勢を変えて空きへ収める例です。Tスピンという名称ではなく、回転候補が成立する形として掲載します。",
      "青色・J型（逆L型）：4つの姿勢がそれぞれ異なります。壁や段差に沿わせるときは、先端がどこに着くかを見てから回転します。",
      "青色・段差回転：青色J型を緑色・黄色の固定地形に沿わせて落とし、姿勢を変えて段差へ差し込む実操作例です。",
      "青色・左壁際入れ込み：青色J型を左壁沿いに落とし、下端で横向きへ回転して足場へ収める実操作例です。",
      "橙色・L型：4つの姿勢がそれぞれ異なります。J型と似ていますが左右が逆なので、壁際では先端位置を確認します。",
      "橙色・下端回転：橙色L型を赤色I型と緑色S型の間へ落とし、下端で回転して縦側を空きへ合わせる例です。",
      "橙色・右壁際入れ込み：橙色L型を右壁へ寄せたまま下降させ、段差の手前で姿勢を変えて底の空きへ入れる例です。",
      "緑色・Z型：紫色S型と左右が逆に見えるため、同じ感覚で回すと失敗しやすいブロックです。回転後の先端位置を必ず確認します。",
      "紫色・S型：緑色Z型と左右が逆です。複数の色付きブロックでできた地形では、回転後にどの段差へ入るかを見て操作します。",
      "紫色・複合地形入れ：青色・橙色で作った2段の地形へ紫色S型を回転させて差し込む、複数ブロック前提の実操作例です。",
      "紫色・複合地形回転：黄色・橙色・青色・水色で組んだ複合地形の右側へ紫色S型を落とし、下部で回転させる実操作例です。",
      "赤色・I型（棒）：回転状態によって成功・失敗が変わるため、棒の向きと空き幅を見て入力します。",
      "赤色・右壁際回転：赤色I型を右壁沿いに縦で落とし、下端で横向きへ回転する壁際の実操作例です。壁に合わせて自由に動くのではなく、候補順の中で成立した位置だけを示しています。",
      "I型は0・2が縦、1・3が横。S型とZ型は0と2、1と3が同じ配置。T・J・L型は4つの姿勢がそれぞれ異なります。",
      "このゲームはSRSではありません。I型だけ別のキック表を使う処理や、壁に合わせて自由に移動する処理はありません。",
      "壁際では『回せる場所を探して自由に動く』のではなく、上記の候補順だけを試します。左2まで試してから右側を試す、という順番もルールの一部です。",
      "床際では下方向の候補も置けないため、一般的な落ちものゲームの感覚で回すと失敗することがあります。壁に密着する前に回し、床際では上の逃げ道を残すのがコツです。",
      "積み上がりの隙間で回す場合も、回転後の4マスが全て空いている必要があります。1マスでも重なれば、その回転は成立しません。",
      "設定から左右移動・落下・右回転・左回転のキーを変更できます。端末では盤面下のタッチ操作も利用できます。",
      "要点は『中心軸』『左1・左2・右1・右2』『必要なときだけ下1』『上方向キックなし』の4つです。形だけでなく、どの候補が先に判定されるかまで覚えると原作どおりに扱えます。",
    ],
  },
  faq: {
    kicker: "FREQUENTLY ASKED QUESTIONS",
    title: "FAQ",
    lines: [
      "Q. ルーム対戦には何が必要？ A. 同じROOM番号または部屋IDを選んで入室します。",
      "Q. ゲーム中の盤面は保存される？ A. P2P対戦ではゲーム中の盤面をサーバーへ送信しません。",
      "Q. 名前や色は保存される？ A. 設定した内容はこの端末に保存されます。",
    ],
  },
} as const;

const menuItems: MenuItem[] = [
  {
    mode: "practice",
    icon: "◎",
    title: "1人で練習",
    description: "自分のペースで盤面と操作を磨く",
  },
  {
    mode: "cpu",
    icon: "◆",
    title: "CPUと対戦",
    description: "CPU相手に実戦形式で練習する",
  },
  {
    mode: "rooms",
    icon: "▦",
    title: "マルチルーム",
    description: "ROOM1〜6や個別ルームで待機・対戦",
  },
  {
    mode: "casual",
    icon: "✦",
    title: "カジュアルマッチ",
    description: "60秒待機して1試合だけ対戦",
  },
  {
    mode: "ranked",
    icon: "★",
    title: "ランクマッチ",
    description: "レートをかけて実力を競う",
    badge: "準備中",
  },
  {
    mode: "custom",
    icon: "◇",
    title: "カスタムルーム",
    description: "部屋IDを決めて同じIDの人と対戦",
  },
];

export function HomeMenu({ installable = false, onInstall, onOpenKeyConfig, onSelect }: HomeMenuProps) {
  const [infoTopic, setInfoTopic] = useState<LegacyInfoTopic | null>(null);
  const selectedInfo = infoTopic ? legacyInfo[infoTopic] : null;

  return (
    <main className="home-shell">
      <header className="home-header">
        <span className="version">BWF / APP EDITION</span>
        <div className="home-header-actions">
          <button className="key-config-top" onClick={onOpenKeyConfig} type="button">⌨ 設定</button>
          <span className="home-status"><span className="status-dot" /> ONLINE</span>
        </div>
      </header>

      <nav aria-label="メインメニュー" className="legacy-nav legacy-home-nav">
        <button aria-current={infoTopic === null ? "page" : undefined} className={infoTopic === null ? "is-active" : ""} onClick={() => setInfoTopic(null)} type="button">トップ</button>
        <button className={infoTopic === "howto" ? "is-active" : ""} onClick={() => setInfoTopic("howto")} type="button">遊び方</button>
        <button className={infoTopic === "manners" ? "is-active" : ""} onClick={() => setInfoTopic("manners")} type="button">マナー</button>
        <button className={infoTopic === "ranks" ? "is-active" : ""} onClick={() => setInfoTopic("ranks")} type="button">段位</button>
        <button className={infoTopic === "rooms" ? "is-active" : ""} onClick={() => setInfoTopic("rooms")} type="button">ルーム</button>
        <button className={infoTopic === "rotation" ? "is-active" : ""} onClick={() => setInfoTopic("rotation")} type="button">回転法則</button>
        <button className={infoTopic === "faq" ? "is-active" : ""} onClick={() => setInfoTopic("faq")} type="button">FAQ</button>
      </nav>

      {selectedInfo ? (
        <div aria-labelledby="legacy-info-title" aria-modal="true" className="legacy-info-modal" role="dialog">
          <section className="legacy-info-card">
            <div className="legacy-info-heading">
              <p className="legacy-info-kicker">{selectedInfo.kicker}</p>
              <button aria-label="説明を閉じる" className="legacy-info-close" onClick={() => setInfoTopic(null)} type="button">×</button>
            </div>
            <h2 id="legacy-info-title">{selectedInfo.title}</h2>
            {infoTopic === "rotation" ? (
              <div aria-label="回転法則のテトリミノGIF" className="legacy-rotation-reference">
                <div className="legacy-rotation-reference-grid">
                  {rotationPieces.map(({ id, name, src }) => (
                    <button
                      aria-label={`${name}の説明へ移動`}
                      className="legacy-rotation-reference-item"
                      key={id}
                      onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      type="button"
                    >
                      <img alt={`${name}の基本形`} src={src} />
                      <span className="legacy-rotation-reference-label">{name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {infoTopic === "rotation" ? (
              <a className="rotation-board-editor-link" href="/?tool=board-editor">
                盤面エディタを別ページで開く ↗
              </a>
            ) : null}
            <div className="legacy-info-body">
              {infoTopic === "rotation" ? (
                <section aria-labelledby="rotation-theory-title" className="legacy-rotation-theory">
                  <h3 id="rotation-theory-title">理論</h3>
                  {legacyInfo.rotation.theory.map((line) => <p key={line}>{line}</p>)}
                </section>
              ) : null}
              {selectedInfo.lines.map((line) => {
                const target = infoTopic === "rotation" ? rotationTargetForLine(line) : undefined;
                const technique = infoTopic === "rotation" ? rotationTechniqueForLine(line) : undefined;
                const piece = target ? rotationPieces.find(({ id }) => id === target) : undefined;
                const anchor = target ?? technique?.id;
                return (
                  <p className={piece ? "legacy-rotation-explanation" : technique ? "legacy-rotation-technique" : undefined} id={anchor} key={line}>
                    {piece ? <img alt={`${piece.name}のGIF`} src={piece.src} /> : null}
                    {technique ? <img alt={`${technique.label}の簡易GIF`} className="legacy-rotation-technique-gif" src={`${technique.src}?v=3`} /> : null}
                    <span>{line}</span>
                  </p>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}

      <section aria-labelledby="home-title" className="home-panel">
        <p className="brand-kicker">block wakuwaku kingdom / 2011 rules</p>
        <h1 className="brand-title home-tagline" id="home-title">
          歯向かうのか？
          <br />
          落ちものブロックゲームで勝負だ！
        </h1>
        <p className="brand-subtitle">PLAY SELECT</p>

        <nav aria-label="ゲームモード" className="mode-menu">
          {menuItems.map(({ mode, icon, title, description, badge }) => (
            <button className="mode-card" key={mode} onClick={() => onSelect(mode)} type="button">
              <span aria-hidden="true" className="mode-icon">{icon}</span>
              <span className="mode-copy">
                <span className="mode-title-row">
                  <strong>{title}</strong>
                  {badge ? <small className="mode-badge">{badge}</small> : null}
                </span>
                <span className="mode-description">{description}</span>
              </span>
              <span aria-hidden="true" className="mode-arrow">›</span>
            </button>
          ))}
        </nav>

        {installable && onInstall ? (
          <button className="install-button" onClick={onInstall} type="button">
            ＋ ホーム画面にアプリを追加
          </button>
        ) : (
          <p className="install-hint">スマホの共有メニューから「ホーム画面に追加」できます</p>
        )}
      </section>

      <AdSlot format="horizontal" label="ホームの広告スペース" />
      <p className="home-footnote">P2P対戦ではゲーム中の盤面をサーバーへ送信しません</p>
    </main>
  );
}
